import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ContasPagarReceberService } from '../../services/dashboard/contas-pagar-receber.service';
import { TituloDTO } from '../../models/dashboard/titulo.model';
import { TotaisContasDTO, ValorPorMoedaDTO } from '../../models/dashboard/totais-contas.model';
import { ContaService } from '../../services/conta/conta.service';
import { ContaDTO, ContaResponseDTO } from '../../models/gerenciar-contas/conta.model';
import { PeriodoFiltro } from '../../models/dashboard/periodo-filtro.model';
import { fmtDateTime } from '../../shared/utils/periodo.utils';
import { PaginatorState } from 'primeng/paginator';

type TipoOperacao = 'ENTRADA' | 'SAIDA';
type SituacaoTitulo = 'ABERTO' | 'PAGO' | 'CANCELADO';

interface CardResumo {
  simbolo: string;
  valor: number;
  label: string;
}

@Component({
  selector: 'app-contas-a-receber',
  templateUrl: './contas-a-receber.component.html',
  styleUrls: ['./contas-a-receber.component.scss'],
})
export class ContasAReceberComponent implements OnInit, OnDestroy {

  // --- Toggle Receber/Pagar ---
  tipoSelecionado: TipoOperacao = 'ENTRADA';
  tipoOptions: { label: string; value: TipoOperacao }[] = [];

  // --- Situacao ---
  situacaoSelecionada: SituacaoTitulo = 'ABERTO';
  situacaoOptions: { label: string; value: SituacaoTitulo }[] = [];

  // --- Periodo (navegacao por mes) ---
  mesAtual: Date = new Date();
  periodoSelecionado!: PeriodoFiltro;
  usandoPeriodoCustomizado = false;

  // --- Conta bancaria ---
  contaSelecionada: number | null = null;
  contas: { label: string; value: number; flagUrl?: string }[] = [];
  loadingContas = false;

  // --- Busca ---
  searchNome = '';
  private searchSubject = new Subject<string>();

  // --- Dados ---
  titulos: TituloDTO[] = [];
  expandedRows: { [key: number]: boolean } = {};
  totais: TotaisContasDTO | null = null;
  totalRecords = 0;

  // --- Paginacao ---
  first = 0;
  rows = 10;
  page = 0;

  // --- Loading ---
  loading = false;
  loadingTotais = false;

  // --- Cards resumo ---
  cardsAberto: CardResumo[] = [];
  cardsRecebido: CardResumo[] = [];

  // --- Compatibilidade queryParam ---
  pessoaId: number | null = null;

  // --- Traducoes ---
  texts = {
    titulo: '',
    subtitulo: '',
  };

  labels = {
    a_receber: '',
    recebidos: '',
    a_pagar: '',
    pagos: '',
    cancelados: '',
    nome_pessoa: '',
    primeiro_vencimento: '',
    valor_recebido: '',
    valor_devolvido: '',
    total: '',
    aberto: '',
    pago: '',
    cancelado: '',
    todas_as_contas: '',
    guarani: '',
    real: '',
    dolar: '',
    numero_titulo: '',
    vencimento: '',
    saldo: '',
    valor: '',
    recebido: '',
    pagamentos_titulo: '',
    data_pagamento: '',
    valor_pago: '',
  };

  botoes = {
    receber: '',
    pagar: '',
  };

  placeholders = {
    buscar_pessoa: '',
  };

  mensagens = {
    nenhuma_pessoa: '',
  };

  toggle = {
    contas_a_receber: '',
    contas_a_pagar: '',
  };

  private destroy$ = new Subject<void>();
  private langChangeSub!: Subscription;

  constructor(
    private contasPagarReceberService: ContasPagarReceberService,
    private contaService: ContaService,
    private messageService: MessageService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    // Extrair pessoaId do queryParam (compatibilidade)
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const pid = params['pessoaId'];
      this.pessoaId = pid ? Number(pid) : null;
    });

    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });

    // Debounce da busca por nome
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.onFiltroChange();
    });

    // Periodo padrao inicial: "Mostrar todos" (showAll), alinhado ao mobile
    this.aplicarPeriodoPadrao();

    // Carregar contas do dropdown
    this.carregarContas();

    // Carregar dados iniciais
    this.carregarDados();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  // --- Traducoes ---

  private loadTranslations(): void {
    this.toggle = {
      contas_a_receber: this.translate.instant('contas-cobrar.toggle.contas_a_receber'),
      contas_a_pagar: this.translate.instant('contas-cobrar.toggle.contas_a_pagar'),
    };

    this.tipoOptions = [
      { label: this.toggle.contas_a_receber, value: 'ENTRADA' },
      { label: this.toggle.contas_a_pagar, value: 'SAIDA' },
    ];

    this.labels = {
      a_receber: this.translate.instant('contas-cobrar.labels.a_receber'),
      recebidos: this.translate.instant('contas-cobrar.labels.recebidos'),
      a_pagar: this.translate.instant('contas-cobrar.labels.a_pagar'),
      pagos: this.translate.instant('contas-cobrar.labels.pagos'),
      cancelados: this.translate.instant('contas-cobrar.labels.cancelados'),
      nome_pessoa: this.translate.instant('contas-cobrar.labels.nome_pessoa'),
      primeiro_vencimento: this.translate.instant('contas-cobrar.labels.primeiro_vencimento'),
      valor_recebido: this.translate.instant('contas-cobrar.labels.valor_recebido'),
      valor_devolvido: this.translate.instant('contas-cobrar.labels.valor_devolvido'),
      total: this.translate.instant('contas-cobrar.labels.total'),
      aberto: this.translate.instant('contas-cobrar.labels.aberto'),
      pago: this.translate.instant('contas-cobrar.labels.pago'),
      cancelado: this.translate.instant('contas-cobrar.labels.cancelado'),
      todas_as_contas: this.translate.instant('contas-cobrar.labels.todas_as_contas'),
      guarani: this.translate.instant('contas-cobrar.labels.guarani'),
      real: this.translate.instant('contas-cobrar.labels.real'),
      dolar: this.translate.instant('contas-cobrar.labels.dolar'),
      numero_titulo: this.translate.instant('contas-cobrar.labels.numero_titulo'),
      vencimento: this.translate.instant('contas-cobrar.labels.vencimento'),
      saldo: this.translate.instant('contas-cobrar.labels.saldo'),
      valor: this.translate.instant('contas-cobrar.labels.valor'),
      recebido: this.translate.instant('contas-cobrar.labels.valor_recebido'),
      pagamentos_titulo: this.translate.instant('contas-cobrar.labels.pagamentos_titulo'),
      data_pagamento: this.translate.instant('contas-cobrar.labels.data_pagamento'),
      valor_pago: this.translate.instant('contas-cobrar.labels.valor_pago'),
    };

    this.situacaoOptions = [
      { label: this.labels.aberto, value: 'ABERTO' },
      { label: this.labels.pago, value: 'PAGO' },
      { label: this.labels.cancelado, value: 'CANCELADO' },
    ];

    this.placeholders = {
      buscar_pessoa: this.translate.instant('contas-cobrar.placeholders.buscar_pessoa'),
    };

    this.mensagens = {
      nenhuma_pessoa: this.translate.instant('contas-cobrar.mensagens.nenhuma_pessoa'),
    };

    this.atualizarTituloSubtitulo();
    this.atualizarCardsResumo();
  }

  private atualizarTituloSubtitulo(): void {
    if (this.tipoSelecionado === 'ENTRADA') {
      this.texts = {
        titulo: this.translate.instant('contas-cobrar.texts.titulo_receber'),
        subtitulo: this.translate.instant('contas-cobrar.texts.subtitulo_receber'),
      };
      this.botoes = {
        receber: this.translate.instant('contas-cobrar.botoes.receber'),
        pagar: this.translate.instant('contas-cobrar.botoes.pagar'),
      };
    } else {
      this.texts = {
        titulo: this.translate.instant('contas-cobrar.texts.titulo_pagar'),
        subtitulo: this.translate.instant('contas-cobrar.texts.subtitulo_pagar'),
      };
      this.botoes = {
        receber: this.translate.instant('contas-cobrar.botoes.receber'),
        pagar: this.translate.instant('contas-cobrar.botoes.pagar'),
      };
    }
  }

  // --- Periodo / Navegacao por mes ---

  get mesAnoLabel(): string {
    const locale = this.translate.currentLang === 'es' ? 'es-PY' : 'pt-BR';
    const label = this.mesAtual.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  mesAnterior(): void {
    this.mesAtual = new Date(this.mesAtual.getFullYear(), this.mesAtual.getMonth() - 1, 1);
    this.usandoPeriodoCustomizado = false;
    this.recalcularPeriodo();
    this.carregarDados();
  }

  proximoMes(): void {
    this.mesAtual = new Date(this.mesAtual.getFullYear(), this.mesAtual.getMonth() + 1, 1);
    this.usandoPeriodoCustomizado = false;
    this.recalcularPeriodo();
    this.carregarDados();
  }

  recalcularPeriodo(): void {
    const inicio = new Date(this.mesAtual.getFullYear(), this.mesAtual.getMonth(), 1, 0, 0);
    const fim = new Date(this.mesAtual.getFullYear(), this.mesAtual.getMonth() + 1, 0, 23, 59);
    this.periodoSelecionado = {
      tipo: 'thisMonth',
      dataInicio: fmtDateTime(inicio),
      dataFim: fmtDateTime(fim),
      texto: this.mesAnoLabel,
    };
  }

  /**
   * Define o periodo INICIAL da tela como "Mostrar todos" (showAll), alinhado
   * ao app mobile e ao valorPadrao do app-periodo-filtro. Intervalo fixo
   * 01/01/2000 00:00 -> 31/12/3000 23:59. A navegacao por mes (mesAnterior/
   * proximoMes) continua usando recalcularPeriodo().
   */
  aplicarPeriodoPadrao(): void {
    const inicio = new Date(2000, 0, 1, 0, 0);
    const fim = new Date(3000, 11, 31, 23, 59);
    this.periodoSelecionado = {
      tipo: 'showAll',
      dataInicio: fmtDateTime(inicio),
      dataFim: fmtDateTime(fim),
      texto: this.translate.instant('dashboard.filtros.mostrar_todos'),
    };
  }

  onPeriodoChange(periodo: PeriodoFiltro): void {
    this.periodoSelecionado = periodo;
    this.usandoPeriodoCustomizado = true;
    this.onFiltroChange();
  }

  // --- Toggle ---

  onTipoChange(): void {
    this.first = 0;
    this.page = 0;
    this.atualizarTituloSubtitulo();
    this.carregarDados();
  }

  // --- Contas dropdown ---

  carregarContas(): void {
    this.loadingContas = true;
    this.contaService.get('/conta').subscribe({
      next: (response: ContaResponseDTO) => {
        this.contas = (response.content || []).map((c: ContaDTO) => ({
          label: Number(c.numeroConta) === 0 ? `${c.tituloDaConta} - EFETIVO` : c.tituloDaConta,
          value: c.id as number,
          flagUrl: this.getFlagUrlByMoeda(c.moeda),
        }));
        this.loadingContas = false;
      },
      error: () => {
        this.loadingContas = false;
      },
    });
  }

  getFlagUrlByMoeda(moeda: ContaDTO['moeda']): string {
    const moedaEnum = (typeof moeda === 'object' && moeda?.enum ? moeda.enum : moeda || '').toString().toUpperCase();
    switch (moedaEnum) {
      case 'GUARANI': return 'assets/images/paraguay-flag.jpg';
      case 'REAL': return 'assets/images/br-flag.svg';
      case 'DOLAR': return 'assets/images/usa-flag.svg';
      case 'PESO': return 'assets/images/argentina-flag.svg';
      default: return '';
    }
  }

  getContaFlagUrl(contaId: number): string {
    const conta = this.contas.find(c => c.value === contaId);
    return conta?.flagUrl || '';
  }

  getContaLabel(contaId: number): string {
    const conta = this.contas.find(c => c.value === contaId);
    return conta?.label || '';
  }

  // --- Carregar dados ---

  carregarDados(): void {
    this.carregarListagem();
    this.carregarTotais();
  }

  private carregarListagem(): void {
    this.loading = true;
    this.contasPagarReceberService.buscarTitulos({
      page: this.page,
      tipoOperacaoFinanceira: this.tipoSelecionado,
      dataHoraInicial: this.periodoSelecionado.dataInicio,
      dataHoraFinal: this.periodoSelecionado.dataFim,
      situacao: this.situacaoSelecionada,
      nomePessoa: this.searchNome || undefined,
      conta: this.contaSelecionada || undefined,
    }).subscribe({
      next: (response) => {
        this.titulos = response.content;
        this.totalRecords = response.totalElements;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro_servidor'),
        });
      },
    });
  }

  private carregarTotais(): void {
    this.loadingTotais = true;
    this.contasPagarReceberService.buscarTotais({
      dataHoraInicial: this.periodoSelecionado.dataInicio,
      dataHoraFinal: this.periodoSelecionado.dataFim,
      tipoOperacaoFinanceira: 'ENTRADA',
      situacao: this.situacaoSelecionada,
      nomePessoa: this.searchNome || undefined,
      conta: this.contaSelecionada || undefined,
      pessoaId: this.pessoaId || undefined,
    }).subscribe({
      next: (response) => {
        this.totais = response;
        this.atualizarCardsResumo();
        this.loadingTotais = false;
      },
      error: () => {
        this.loadingTotais = false;
      },
    });
  }

  // --- Cards resumo ---

  private atualizarCardsResumo(): void {
    if (!this.totais) return;

    let abertoData: ValorPorMoedaDTO | undefined;
    let recebidoData: ValorPorMoedaDTO | undefined;
    let labelAberto: string;
    let labelRecebido: string;

    if (this.tipoSelecionado === 'ENTRADA') {
      // Quando a situacao for CANCELADO, exibimos os totais cancelados no
      // primeiro card de "em aberto" para reaproveitar o layout existente.
      if (this.situacaoSelecionada === 'CANCELADO') {
        abertoData = this.totais.totalContaAReceberCanceladas;
        recebidoData = undefined;
        labelAberto = this.labels.cancelados || this.labels.cancelado;
        labelRecebido = '';
      } else {
        abertoData = this.totais.totalContaAReceberAbertas;
        recebidoData = this.totais.totalContaAReceberRecebidas;
        labelAberto = this.labels.a_receber;
        labelRecebido = this.labels.recebidos;
      }
    } else {
      if (this.situacaoSelecionada === 'CANCELADO') {
        abertoData = this.totais.totalContaAPagarCanceladas;
        recebidoData = undefined;
        labelAberto = this.labels.cancelados || this.labels.cancelado;
        labelRecebido = '';
      } else {
        abertoData = this.totais.totalContaAPagarAbertas;
        recebidoData = this.totais.totalContaAPagarPagas;
        labelAberto = this.labels.a_pagar;
        labelRecebido = this.labels.pagos;
      }
    }

    this.cardsAberto = abertoData ? this.montarCards(abertoData, labelAberto) : [];
    this.cardsRecebido = recebidoData ? this.montarCards(recebidoData, labelRecebido) : [];
  }

  private montarCards(dados: ValorPorMoedaDTO, _label: string): CardResumo[] {
    if (!dados) return [];
    return [
      { simbolo: 'G$', valor: dados.valorTotalEmGuarani, label: this.labels.guarani },
      { simbolo: 'R$', valor: dados.valorTotalEmReal, label: this.labels.real },
      { simbolo: '$', valor: dados.valorTotalEmDolar, label: this.labels.dolar },
    ];
  }

  // --- Labels de cards resumo ---

  get labelAberto(): string {
    if (this.situacaoSelecionada === 'CANCELADO') {
      return this.labels.cancelados || this.labels.cancelado;
    }
    return this.tipoSelecionado === 'ENTRADA' ? this.labels.a_receber : this.labels.a_pagar;
  }

  get labelRecebido(): string {
    return this.tipoSelecionado === 'ENTRADA' ? this.labels.recebidos : this.labels.pagos;
  }

  get iconAberto(): string {
    return this.tipoSelecionado === 'ENTRADA' ? 'pi pi-clock' : 'pi pi-clock';
  }

  get iconRecebido(): string {
    return 'pi pi-check-circle';
  }

  // --- Cor da borda do card de pessoa ---

  get bordaCorClasse(): string {
    return this.tipoSelecionado === 'ENTRADA' ? 'borda-entrada' : 'borda-saida';
  }

  // --- Eventos de filtros ---

  onFiltroChange(): void {
    this.first = 0;
    this.page = 0;
    this.carregarDados();
  }

  onSearchNomeInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchNome = value;
    this.searchSubject.next(value);
  }

  // --- Paginacao ---

  onPageChange(event: PaginatorState): void {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? 10;
    this.page = Math.floor(this.first / this.rows);
    this.carregarListagem();
  }

  // --- Drill-down ---

  abrirPessoa(titulo: TituloDTO): void {
    const pid = titulo.pessoaId || titulo.pessoa_id || this.pessoaId;
    if (!pid) return;
    const rota = this.tipoSelecionado === 'SAIDA'
      ? ['/contas-a-pagar/pessoa', pid]
      : ['/contas-a-receber/pessoa', pid];
    this.router.navigate(rota, {
      queryParams: { nome: titulo.nomePessoa || '' },
    });
  }

  // --- Calculo de total pago ---

  calcularTotalPago(titulo: TituloDTO): number {
    if (!titulo.tipoDeCobranca || titulo.tipoDeCobranca.length === 0) return 0;
    return titulo.tipoDeCobranca.reduce((sum, tc) => sum + (tc.valorMovimento || tc.valor), 0);
  }

  temPagamentos(titulo: TituloDTO): boolean {
    return !!titulo.tipoDeCobranca && titulo.tipoDeCobranca.length > 0;
  }

  toggleExpand(titulo: TituloDTO): void {
    if (this.expandedRows[titulo.id]) {
      delete this.expandedRows[titulo.id];
    } else {
      this.expandedRows[titulo.id] = true;
    }
  }

  // --- Helpers de moeda ---

  getMoedaSimbolo(moeda: any): string {
    const moedaStr = (typeof moeda === 'object' && moeda?.enum ? moeda.enum : moeda || '').toString().toUpperCase();
    switch (moedaStr) {
      case 'GUARANI': return 'G$';
      case 'REAL': return 'R$';
      case 'DOLAR': return '$';
      default: return '';
    }
  }

  // --- Formatar data ---

  formatarVencimento(vencimento: string): string {
    if (!vencimento) return '-';
    try {
      const date = new Date(vencimento);
      if (isNaN(date.getTime())) return vencimento;
      const lang = this.translate.currentLang || 'pt-BR';
      const locale = lang === 'es' ? 'es-PY' : 'pt-BR';
      return date.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return vencimento;
    }
  }
}
